import Component from 'flarum/common/Component';
import { stocksOf, ratesOf } from '../util';
const SYM = {
  NGN: '₦',
  USD: '$',
  GBP: '£',
  EUR: '€',
  CAD: 'C$',
  AUD: 'A$',
  NZD: 'NZ$',
  AED: 'AED '
};
const NAME = {
  NGN: '₦ Naira',
  USD: '$ US dollar',
  GBP: '£ Pound',
  EUR: '€ Euro',
  CAD: 'C$ Canadian dollar',
  AUD: 'A$ Australian dollar',
  NZD: 'NZ$ New Zealand dollar',
  AED: 'AED Dirham'
};

/**
 * Share converter: money (naira or another currency) -> whole shares with what's left over, or shares -> money,
 * at the latest price and rate. attrs: markets, symbol (fixes the stock, on a company page). Excludes fees.
 */
export default class Converter extends Component {
  oninit(vnode) {
    super.oninit(vnode);
    this.c = {
      symbol: vnode.attrs.symbol || '',
      mode: 'naira',
      amount: '',
      shares: '',
      currency: 'NGN'
    };
  }
  view() {
    const stocks = stocksOf(this.attrs.markets);
    if (!stocks.length) return null;
    const c = this.c;
    const fixed = !!this.attrs.symbol;
    if (fixed) c.symbol = this.attrs.symbol;
    const stock = stocks.find(s => s.symbol === c.symbol) || stocks[0];
    c.symbol = stock.symbol;
    const price = Number(stock.current_price);
    const num = v => {
      const n = parseFloat(String(v).replace(/[^0-9.]/g, ''));
      return isFinite(n) && n > 0 ? n : 0;
    };
    const naira = n => '₦' + n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    const whole = n => n.toLocaleString('en-US', {
      maximumFractionDigits: 0
    });

    // naira per unit of each currency; the dirham from the US dollar (pegged at 3.6725 per dollar)
    const rates = ratesOf(this.attrs.markets);
    const fx = {
      NGN: 1
    };
    ['USD', 'GBP', 'EUR', 'CAD', 'AUD', 'NZD'].forEach(k => fx[k] = Number(rates[k]) || 0);
    fx.AED = Number(rates.AED) || (fx.USD ? fx.USD / 3.6725 : 0);
    if (!fx[c.currency]) c.currency = 'NGN';
    const rate = fx[c.currency];
    const money = nairaValue => c.currency === 'NGN' ? naira(nairaValue) : SYM[c.currency] + (nairaValue / rate).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }) + ' (' + naira(nairaValue) + ')';
    let result = null;
    if (c.mode === 'naira' && num(c.amount)) {
      const amount = num(c.amount) * rate;
      const units = Math.floor(amount / price);
      const cost = units * price;
      result = units > 0 ? [m('.VouchaConv-big', whole(units) + ' share' + (units === 1 ? '' : 's') + ' of ' + stock.symbol), m('.VouchaConv-sub', 'Cost ' + money(cost) + ' · left over ' + money(amount - cost))] : [m('.VouchaConv-big', 'Not enough for 1 share'), m('.VouchaConv-sub', '1 share of ' + stock.symbol + ' costs ' + money(price))];
    } else if (c.mode === 'shares' && num(c.shares)) {
      const units = Math.floor(num(c.shares));
      result = [m('.VouchaConv-big', money(units * price)), m('.VouchaConv-sub', whole(units) + ' share' + (units === 1 ? '' : 's') + ' of ' + stock.symbol + ' at ' + money(price))];
    }
    const rateNote = c.currency === 'NGN' ? '' : ' 1 ' + c.currency + ' = ' + naira(rate) + (c.currency === 'AED' && !Number(rates.AED) ? ' (from the US dollar rate; the dirham is pegged at 3.6725 per dollar).' : ' (latest rate).');
    return m('section.VouchaMarkets-section.VouchaConv', [m('h2', fixed ? 'What ' + stock.symbol + ' shares cost' : 'Share converter'), m('p.VouchaConv-intro', fixed ? 'How many ' + stock.symbol + ' shares your money buys, in naira or another currency, or what a number of shares costs.' : 'How many NGX shares can your money buy, in naira or another currency, or what do shares cost? At the latest prices and rates.'), m('.VouchaConv-row', [fixed ? null : m('label.VouchaConv-field', [m('span', 'Stock'), m('select', {
      value: c.symbol,
      onchange: e => c.symbol = e.target.value
    }, stocks.map(s => m('option', {
      value: s.symbol
    }, s.symbol + ' · ' + s.name)))]), m('.VouchaConv-modes', {
      role: 'group',
      'aria-label': 'Convert from'
    }, [m('button', {
      type: 'button',
      className: c.mode === 'naira' ? 'is-on' : '',
      onclick: () => c.mode = 'naira'
    }, 'Money → shares'), m('button', {
      type: 'button',
      className: c.mode === 'shares' ? 'is-on' : '',
      onclick: () => c.mode = 'shares'
    }, 'Shares → money')]), m('label.VouchaConv-field.VouchaConv-currency', [m('span', 'Currency'), m('select', {
      value: c.currency,
      onchange: e => c.currency = e.target.value
    }, Object.keys(fx).filter(k => fx[k]).map(k => m('option', {
      value: k
    }, NAME[k])))]), c.mode === 'naira' ? m('label.VouchaConv-field', [m('span', 'Amount (' + SYM[c.currency].trim() + ')'), m('input', {
      type: 'text',
      inputmode: 'decimal',
      placeholder: 'e.g. 500,000',
      value: c.amount,
      oninput: e => c.amount = e.target.value
    })]) : m('label.VouchaConv-field', [m('span', 'Number of shares'), m('input', {
      type: 'text',
      inputmode: 'numeric',
      placeholder: 'e.g. 1,000',
      value: c.shares,
      oninput: e => c.shares = e.target.value
    })])]), m('.VouchaConv-result', {
      'aria-live': 'polite'
    }, result || m('.VouchaConv-sub', 'Enter ' + (c.mode === 'naira' ? 'an amount' : 'a number of shares') + ' to see the result.')), m('p.VouchaConv-note', stock.symbol + ' last price ' + naira(price) + '.' + rateNote + ' Whole shares only; excludes broker fees, levies, charges and currency conversion costs. Not investment advice.')]);
  }
}
flarum.reg.add('voucha-markets', 'forum/components/Converter', Converter);